// The figure/asset library tab. Two methods coexist, feeding/reading ONE
// project-scoped figure library:
//   • Method 1 — AI-find a figure from the sources + browse the shared project
//     pool (extracted once, organised by source folder) + upload your own.
//   • Method 2 — bulk-capture every figure from the sources (cheap raw extraction
//     + free local CLIP embed) and search the library semantically by what a
//     figure shows, or filter by source file.
// Any asset can be inserted into the document.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addReportAssetFromPool,
  captureProjectFigures,
  deleteReportAsset,
  findReportFigure,
  getFigureCaptureStatus,
  insertReportAsset,
  listProjectFigures,
  listReportAssets,
  uploadReportAsset,
  type ReportAsset,
  type ReportFileSurvey,
  type ReportPoolFigure,
} from "@/api/report";
import { Alert, Button, Spinner } from "@/components/ui";
import { AssetCard } from "./asset-card";

interface Props {
  projectId: string;
  assets: ReportAsset[];
  files: ReportFileSurvey[];        // for the by-file filter
  onChanged: () => void;            // refetch the report (assets live on it)
  onInserted: () => void;           // report doc changed (latex updated)
  /** assetId → free-text instruction: "make a label-the-diagram question here".
   *  Applied on the next study-set generation. */
  labelReqs?: Record<string, string>;
  onLabelReqs?: (next: Record<string, string>) => void;
}

/** Group pool figures by their source folder (mirrors the upload hierarchy). */
function groupByFolder(pool: ReportPoolFigure[]): [string, ReportPoolFigure[]][] {
  const map = new Map<string, ReportPoolFigure[]>();
  for (const f of pool) {
    const k = f.folder_path || "";
    (map.get(k) ?? map.set(k, []).get(k)!).push(f);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function FigureLibraryPanel({
  projectId, assets, files, onChanged, onInserted, labelReqs, onLabelReqs,
}: Props) {
  const { t } = useTranslation();
  const setLabelReq = (assetId: string, next: { on: boolean; instruction: string }) => {
    if (!onLabelReqs) return;
    const map = { ...(labelReqs ?? {}) };
    if (next.on) map[assetId] = next.instruction;
    else delete map[assetId];
    onLabelReqs(map);
  };
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [searchText, setSearchText] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [fileFilter, setFileFilter] = useState("");
  const [caption, setCaption] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pool, setPool] = useState<ReportPoolFigure[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Method 2 — the project figure library (CLIP search + by-file filter).
  const { data: figs } = useQuery({
    queryKey: ["project-figures", projectId, fileFilter, submitted],
    queryFn: () =>
      listProjectFigures(projectId, {
        source_file_id: fileFilter || undefined,
        q: submitted || undefined,
      }),
  });
  const invalidateFigures = () =>
    qc.invalidateQueries({ queryKey: ["project-figures", projectId] });

  // Capture status — poll while a capture is running, then refresh the library.
  const { data: capStatus } = useQuery({
    queryKey: ["figure-capture", projectId],
    queryFn: () => getFigureCaptureStatus(projectId),
    refetchInterval: (q) =>
      q.state.data?.figure_capture_status === "running" ? 2500 : false,
  });
  const capturing = capStatus?.figure_capture_status === "running";
  const prevStatus = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const s = capStatus?.figure_capture_status;
    if (prevStatus.current === "running" && s !== "running") {
      invalidateFigures();
      onChanged();
    }
    prevStatus.current = s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capStatus]);

  const refreshPool = async () => {
    try {
      const res = await listReportAssets(projectId);
      setPool(res.pool ?? []);
    } catch {
      /* pool is a bonus — leave whatever we have */
    }
  };

  // Refresh the pool on mount and whenever the materialized assets change (an
  // add/insert/delete flips a pool figure's `added` flag).
  useEffect(() => {
    void refreshPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, assets.length]);

  const run = async (fn: () => Promise<unknown>, after: () => void) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      after();
      await refreshPool();
      invalidateFigures();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  const startCapture = (onlyMarked: boolean) =>
    run(
      () => captureProjectFigures(projectId, { only_marked: onlyMarked, caption }),
      () => qc.invalidateQueries({ queryKey: ["figure-capture", projectId] }),
    );

  const folders = useMemo(() => groupByFolder(pool), [pool]);
  // The library grid reflects capture + search/filter; falls back to the report's
  // own assets until the query resolves.
  const library = figs?.figures ?? assets;
  const searching = Boolean(submitted) || Boolean(fileFilter);

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-3">
      {/* Method 2 — bulk-capture the project's figures into the library. */}
      <div className="space-y-2">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t("Capture figures from your sources")}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy || capturing} onClick={() => startCapture(false)}>
            {capturing ? <Spinner className="h-3 w-3" /> : t("Capture all figures")}
          </Button>
          <Button size="sm" variant="secondary" disabled={busy || capturing} onClick={() => startCapture(true)}>
            {t("Capture marked only")}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-soft">
          <input type="checkbox" checked={caption} onChange={(e) => setCaption(e.target.checked)} />
          {t("AI caption & tag (uses tokens)")}
        </label>
        {capturing && <p className="text-xs text-ink-faint">{t("Capturing figures…")}</p>}
      </div>

      {/* Semantic / by-file search over the captured library (free, local CLIP). */}
      <div className="space-y-2 border-t border-black/5 pt-2">
        <div className="flex gap-2">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSubmitted(searchText.trim());
            }}
            placeholder={t("Search figures by what they show…")}
            className="flex-1 rounded-lg border border-black/10 px-2 py-1 text-sm focus:border-brand-400 focus:outline-none"
          />
          <Button size="sm" onClick={() => setSubmitted(searchText.trim())}>
            {t("Search")}
          </Button>
        </div>
        <select
          value={fileFilter}
          onChange={(e) => setFileFilter(e.target.value)}
          className="w-full rounded-lg border border-black/10 px-2 py-1 text-xs"
        >
          <option value="">{t("All source files")}</option>
          {files.map((f) => (
            <option key={f.id} value={f.id}>
              {f.filename}
            </option>
          ))}
        </select>
      </div>

      {/* Method 1 — AI-find a specific figure in the sources. */}
      <div className="border-t border-black/5 pt-2">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t("Find a figure in the sources")}
        </p>
        <div className="flex gap-2">
          <input
            value={query}
            disabled={busy}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim())
                run(() => findReportFigure(projectId, { query: query.trim() }), onChanged);
            }}
            placeholder={t("e.g. the 3He–4He phase diagram")}
            className="flex-1 rounded-lg border border-black/10 px-2 py-1 text-sm focus:border-brand-400 focus:outline-none"
          />
          <Button
            size="sm"
            disabled={busy || !query.trim()}
            onClick={() => run(() => findReportFigure(projectId, { query: query.trim() }), onChanged)}
          >
            {busy ? <Spinner className="h-3 w-3" /> : t("Find")}
          </Button>
        </div>
      </div>

      <div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) run(() => uploadReportAsset(projectId, f), onChanged);
            e.target.value = "";
          }}
        />
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => fileInput.current?.click()}>
          {t("Upload an image")}
        </Button>
      </div>

      {error && <Alert>{error}</Alert>}

      {/* Shared project figure pool — extracted once, organised by source folder. */}
      {pool.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
            {t("From your sources")}
          </p>
          <div className="flex flex-col gap-2">
            {folders.map(([folder, figs]) => (
              <div key={folder || "__root"}>
                <p className="mb-1 truncate text-[11px] font-medium text-ink-faint" title={folder || t("Project root")}>
                  📁 {folder || t("Project root")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {figs.map((f) => (
                    <PoolFigureCard
                      key={f.key}
                      figure={f}
                      busy={busy}
                      onAdd={() =>
                        run(() => addReportAssetFromPool(projectId, { key: f.key }), onChanged)
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
          {searching ? t("Matching figures") : t("Figure library")}
        </p>
        {library.length === 0 ? (
          <p className="text-sm text-ink-faint">
            {searching
              ? t("No figures match. Try capturing figures or a different search.")
              : t("No figures yet. Capture from your sources, find one, or upload your own.")}
          </p>
        ) : (
          <>
          {onLabelReqs && Object.keys(labelReqs ?? {}).length > 0 && (
            <p className="mb-2 rounded-md bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
              {t("{{n}} label-the-diagram question(s) requested — applied on the next “Generate study set”.",
                { n: Object.keys(labelReqs ?? {}).length })}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {library.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                busy={busy}
                onInsert={() => run(() => insertReportAsset(projectId, a.id, {}), onInserted)}
                onDelete={() => run(() => deleteReportAsset(projectId, a.id), onChanged)}
                labelReq={onLabelReqs
                  ? { on: a.id in (labelReqs ?? {}), instruction: labelReqs?.[a.id] ?? "" }
                  : undefined}
                onLabelReq={onLabelReqs ? (next) => setLabelReq(a.id, next) : undefined}
              />
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

/** A browsable pool figure with an "Add to report" / "Added" action. */
function PoolFigureCard({
  figure,
  onAdd,
  busy,
}: {
  figure: ReportPoolFigure;
  onAdd: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <figure className="m-0 rounded-lg border border-black/10 p-2">
      {figure.url ? (
        <img
          src={figure.url}
          alt={figure.caption || figure.source_filename}
          loading="lazy"
          className="h-28 w-full rounded object-contain"
        />
      ) : (
        <div className="flex h-28 items-center justify-center rounded bg-zinc-100 text-xs text-ink-faint">
          {t("no preview")}
        </div>
      )}
      <figcaption
        className="mt-1 truncate text-[11px] text-ink-faint"
        title={figure.caption || figure.source_filename}
      >
        {figure.caption || figure.source_filename}
      </figcaption>
      <div className="mt-1 flex items-center justify-between">
        <span className="truncate rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-500" title={figure.source_filename}>
          {figure.source_filename || figure.origin}
        </span>
        {figure.added ? (
          <span className="text-[11px] font-medium text-emerald-600">✓ {t("Added")}</span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onAdd}
            className="text-[11px] font-medium text-brand-600 hover:underline"
          >
            {t("Add")}
          </button>
        )}
      </div>
    </figure>
  );
}
